import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Replay, Save, Visibility } from "@material-ui/icons";

import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";
import { notification } from "../../../reducer/home/action";

// Admin editor for system mail templates. Keyed list on the left; subject +
// HTML body editor + live preview on the right. Placeholder reference
// rendered as chips so the admin doesn't have to remember the syntax.

// Well-known templates ship with rich metadata so the UI can show what each
// one does. Unknown keys still appear with raw key/description.
const KNOWN_TEMPLATES = {
  work_approved: {
    label: "Email duyệt công trình",
    description:
      "Gửi cho mọi tác giả khi 1 công trình (bài báo / đề tài / hội thảo / …) được duyệt. Tắt switch \"Đang bật\" ở dưới để không gửi email lúc Phòng QLKH xác nhận bài.",
    placeholders: [
      { token: "{{Name}}", note: "Tên người nhận" },
      { token: "{{Code}}", note: "Mã viên chức người nhận" },
      { token: "{{Email}}", note: "Email người nhận" },
      { token: "{{DepartmentName}}", note: "Khoa / bộ môn" },
      { token: "{{workTitle}}", note: "Tên công trình" },
      { token: "{{workType}}", note: "Loại công trình (Bài báo / Đề tài / …)" },
      { token: "{{approvedAt}}", note: "Thời điểm duyệt (HH:mm dd/MM/yyyy)" },
      { token: "{{approvedBy}}", note: "Tên người duyệt" },
      { token: "{{systemName}}", note: "Tên hệ thống (VIN NCKH)" },
    ],
  },
  work_revision_requested: {
    label: "Email yêu cầu khai lại",
    description:
      "Gửi cho người kê khai (và tác giả nội bộ) khi admin click \"Yêu cầu khai lại\" trên một công trình. Tắt switch ở dưới nếu không muốn gửi.",
    placeholders: [
      { token: "{{Name}}", note: "Tên người nhận" },
      { token: "{{Code}}", note: "Mã viên chức người nhận" },
      { token: "{{Email}}", note: "Email người nhận" },
      { token: "{{DepartmentName}}", note: "Khoa / bộ môn" },
      { token: "{{workTitle}}", note: "Tên công trình" },
      { token: "{{workType}}", note: "Loại công trình" },
      { token: "{{revisionReason}}", note: "Lý do admin yêu cầu khai lại" },
      { token: "{{requestedAt}}", note: "Thời điểm yêu cầu (HH:mm dd/MM/yyyy)" },
      { token: "{{requestedBy}}", note: "Tên admin yêu cầu khai lại" },
      { token: "{{systemName}}", note: "Tên hệ thống (VIN NCKH)" },
    ],
  },
  work_resubmitted: {
    label: "Email đã khai lại — chờ duyệt",
    description:
      "Gửi cho admin yêu cầu khai lại (+ email Phòng QLKH nếu có config) khi người kê khai lưu lại công trình. Tắt switch ở dưới nếu không muốn gửi.",
    placeholders: [
      { token: "{{workTitle}}", note: "Tên công trình" },
      { token: "{{workType}}", note: "Loại công trình" },
      { token: "{{submitterName}}", note: "Tên người khai lại" },
      { token: "{{resubmittedAt}}", note: "Thời điểm khai lại (HH:mm dd/MM/yyyy)" },
      { token: "{{systemName}}", note: "Tên hệ thống (VIN NCKH)" },
    ],
  },
};

// Sample data used to fill placeholders for the preview pane.
const PREVIEW_SUBS = {
  "{{Name}}": "Nguyễn Văn A",
  "{{Code}}": "003.021.01437",
  "{{Email}}": "vana@neu.edu.vn",
  "{{DepartmentName}}": "Khoa Toán Kinh tế",
  "{{workTitle}}": "Tối ưu hoá danh mục đầu tư bằng học máy",
  "{{workType}}": "Bài báo khoa học",
  "{{approvedAt}}": "15:30 28/05/2026",
  "{{approvedBy}}": "Trần Quản Trị",
  "{{revisionReason}}": "Vui lòng cập nhật DOI và đính kèm file PDF bản chính thức.",
  "{{requestedAt}}": "10:15 30/05/2026",
  "{{requestedBy}}": "Trần Quản Trị",
  "{{submitterName}}": "Nguyễn Văn A",
  "{{resubmittedAt}}": "09:20 01/06/2026",
  "{{systemName}}": "VIN NCKH",
};

const renderPreview = (html) => {
  let out = html || "";
  for (const [token, value] of Object.entries(PREVIEW_SUBS)) {
    out = out.split(token).join(value);
  }
  return out;
};

function EmailTemplateForm() {
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");
  const [draft, setDraft] = useState({ key: "", subject: "", bodyHtml: "", description: "", active: true });

  const load = async () => {
    setLoading(true);
    const { response } = await apiCall({ ...API_URLS.SYSTEM_MAIL_TEMPLATE.list() });
    if (response?.data?.success) {
      const list = response.data.data || [];
      setTemplates(list);
      // Auto-pick first known key (work_approved) on first load.
      if (!selectedKey && list.length > 0) {
        setSelectedKey(list[0].key);
        setDraft({ ...list[0] });
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching templates resets the draft to the saved version.
  useEffect(() => {
    if (!selectedKey) return;
    const t = templates.find((x) => x.key === selectedKey);
    if (t) setDraft({ ...t });
  }, [selectedKey, templates]);

  const handleSave = async () => {
    if (!draft.subject?.trim() || !draft.bodyHtml?.trim()) {
      dispatch(notification({ type: "error", message: "Tiêu đề và nội dung không được trống" }));
      return;
    }
    const { response, error } = await apiCall({
      ...API_URLS.SYSTEM_MAIL_TEMPLATE.upsert(),
      payload: {
        key: draft.key,
        subject: draft.subject,
        bodyHtml: draft.bodyHtml,
        description: draft.description || "",
        active: !!draft.active,
      },
    });
    if (!error && response?.data?.success) {
      dispatch(notification({ type: "success", message: "Đã lưu mẫu email" }));
      load();
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Lưu thất bại" }));
    }
  };

  const meta = KNOWN_TEMPLATES[selectedKey] || {};

  return (
    // Parent Card in AdminSetting has fixed height; without scroll here the
    // preview Paper at the bottom would be clipped.
    <Box
      style={{
        maxHeight: "calc(100vh - 170px)",
        overflowY: "auto",
        paddingRight: 8,
      }}
    >
      <Grid container alignItems="center" style={{ marginBottom: 12 }}>
        <Grid item>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Mẫu email hệ thống
          </Typography>
          <Typography variant="caption" style={{ color: "#666" }}>
            Chỉnh sửa nội dung email tự động gửi đi từ hệ thống. Mọi thay đổi áp dụng ngay lần gửi kế tiếp.
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: "auto" }}>
          <Button variant="outlined" startIcon={<Replay />} onClick={load} disabled={loading} style={{ marginRight: 8 }}>
            Tải lại
          </Button>
          <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSave}>
            Lưu thay đổi
          </Button>
        </Grid>
      </Grid>
      <Divider style={{ marginBottom: 16 }} />

      <Grid container spacing={2}>
        {/* Template selector */}
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent style={{ padding: 12 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                Danh sách mẫu
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Chọn mẫu</InputLabel>
                <Select label="Chọn mẫu" value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
                  {templates.map((t) => (
                    <MenuItem key={t.key} value={t.key}>
                      {KNOWN_TEMPLATES[t.key]?.label || t.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {meta.description && (
                <Typography variant="caption" style={{ color: "#666", display: "block", marginTop: 8 }}>
                  {meta.description}
                </Typography>
              )}

              {meta.placeholders && (
                <>
                  <Divider style={{ margin: "12px 0" }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                    Biến có thể dùng
                  </Typography>
                  <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
                    Bấm để copy vào clipboard.
                  </Typography>
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {meta.placeholders.map((p) => (
                      <Tooltip key={p.token} title={p.note} placement="top">
                        <Chip
                          size="small"
                          label={p.token}
                          variant="outlined"
                          clickable
                          onClick={() => {
                            if (navigator.clipboard) navigator.clipboard.writeText(p.token);
                          }}
                          style={{ fontFamily: "monospace", fontSize: 11 }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Editor + preview */}
        <Grid item xs={12} md={9}>
          <Box style={{ marginBottom: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!draft.active}
                  color="primary"
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
              }
              label={draft.active ? "Đang bật — email sẽ được gửi" : "Đang tắt — không gửi"}
            />
          </Box>

          <TextField
            fullWidth
            label="Tiêu đề email"
            variant="outlined"
            size="small"
            margin="dense"
            value={draft.subject || ""}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            helperText="Có thể dùng các biến ở cột trái. VD: [VIN NCKH] Công trình {{workTitle}} đã được duyệt"
          />

          <TextField
            fullWidth
            label="Nội dung HTML"
            variant="outlined"
            size="small"
            margin="dense"
            multiline
            minRows={12}
            maxRows={24}
            value={draft.bodyHtml || ""}
            onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
            inputProps={{ style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 } }}
            helperText="Hỗ trợ HTML đầy đủ. Email client (Outlook / Gmail) render trực tiếp."
          />

          <TextField
            fullWidth
            label="Ghi chú nội bộ (tuỳ chọn)"
            variant="outlined"
            size="small"
            margin="dense"
            value={draft.description || ""}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />

          {/* Live preview */}
          <Paper
            variant="outlined"
            style={{ marginTop: 12, padding: 12, backgroundColor: "#fafafa" }}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
              <Visibility fontSize="small" style={{ color: "#1976d2" }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Xem trước (dùng dữ liệu mẫu)
              </Typography>
            </Box>
            <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
              <b>Tiêu đề:</b> {renderPreview(draft.subject)}
            </Typography>
            <Divider style={{ marginBottom: 8 }} />
            <div
              style={{ backgroundColor: "white", padding: 12, borderRadius: 4 }}
              dangerouslySetInnerHTML={{ __html: renderPreview(draft.bodyHtml) }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EmailTemplateForm;
